/**
 * War bot. The only real decision is whether to raise in a war: a bot raises when it has comfortably more
 * cards than its opponent (it can afford the extra card), and sometimes on a hunch at higher difficulties.
 * Flipping and staying are trivial. The bot only ever reads its own seat's view.
 */
import { chance, type BotContext, type BotEngine } from '../../bots/types.js';
import type { WarAction, WarLegal, WarView } from './engine.js';

function choose(view: WarView, legal: WarLegal, ctx: BotContext): WarAction {
  if (legal.flip) return { type: 'flip' };
  if (!legal.raise || view.seat === null) return { type: 'stay' };
  const ahead = view.counts[view.seat]! - view.counts[1 - view.seat]!;
  // Raising only doubles the stake if the opponent also raises, so it is cheapest when well ahead.
  const p = ahead >= 8 ? 0.8 : ahead >= 0 ? 0.35 : 0.1;
  return chance(ctx, p) ? { type: 'raise' } : { type: 'stay' };
}

export const warBot: BotEngine<WarView, WarLegal, WarAction> = {
  decideAction(view, legal, ctx) {
    if (!legal.flip && legal.raise && chance(ctx, ctx.profile.mistakeRate)) return { type: ctx.rng.next() < 0.5 ? 'raise' : 'stay' };
    return choose(view, legal, ctx);
  },

  evaluateState(view) {
    if (view.seat === null) return 0.5;
    const mine = view.counts[view.seat]!;
    return Math.max(0, Math.min(1, mine / Math.max(1, mine + view.counts[1 - view.seat]!)));
  },

  calculateRisk(_view, legal) {
    return legal.raise ? 0.5 : 0;
  },

  handleTimeout(_view, legal) {
    return legal.flip ? { type: 'flip' } : { type: 'stay' };
  },
};
